// src/data/about-content.ts
import type { Bilingual } from "@/types/home";

/** High-res imagery (Unsplash, verified) sized on demand via the `w` param. */
const U = (id: string, w = 1600) =>
  `https://images.unsplash.com/${id}?auto=format&fit=crop&w=${w}&q=80`;

export const IMG = {
  hero: U("photo-1449824913935-59a10b8d2000", 2000),
  facade: U("photo-1487958449943-2429e8be8625"),
  lobby: U("photo-1497366216548-37526070297c"),
  chairman: U("photo-1560250097-0b93528c311a", 1000),
  director: U("photo-1573496359142-b8d87734a5a2", 1000),
  officer: U("photo-1566492031773-4f4e44671857", 1000),
};

/** Sticky section anchors (mirror the reference sub-nav). */
export interface AboutSection { id: string; label: Bilingual }
export const ABOUT_SECTIONS: AboutSection[] = [
  { id: "tahawer", label: { ar: "تحاور", en: "Tahawer" } },
  { id: "group", label: { ar: "المجموعة", en: "The Group" } },
  { id: "leadership", label: { ar: "القيادة", en: "Leadership" } },
  { id: "partners", label: { ar: "الشركاء", en: "Partners" } },
];

export const ABOUT = {
  eyebrow: { ar: "عن الشركة", en: "About" } as Bilingual,
  title: {
    ar: "نبني المعالم المعمارية التي تُعرّف مستقبل المنصورة المؤسسي.",
    en: "We build the architectural landmarks that define Mansoura's institutional future.",
  } as Bilingual,
  image: IMG.hero,
};

// ── TAHAWER — Our Story ──────────────────────────────────────────────
export const STORY = {
  eyebrow: { ar: "الإرث", en: "Heritage" } as Bilingual,
  title: { ar: "قصتنا", en: "Our Story" } as Bilingual,
  lead: {
    ar: "منذ نشأتها، قادت رؤيةٌ واحدة تحاور: أن نرتقي بأفق المنصورة عبر جودةٍ لا تقبل المساومة ونزاهةٍ معمارية.",
    en: "Since our inception, Tahawer has been guided by a singular vision: to elevate the skyline of Mansoura through uncompromising quality and architectural integrity.",
  } as Bilingual,
  body: {
    ar: "ما بدأ كاستشاريةٍ متخصصة تطوّر إلى مطوّرٍ رائد. رحلتنا محكومةٌ بالتزامٍ بدوام الشكل وكفاءة الوظيفة، بحيث يخدم كل متر نطوّره إرثاً باقياً.",
    en: "What started as a specialized consultancy has evolved into a premier developer. Our journey is defined by a commitment to the permanence of form and the efficiency of function, ensuring every square meter we develop serves a legacy.",
  } as Bilingual,
};

export interface Milestone { year: string; label: Bilingual }
export const TIMELINE: Milestone[] = [
  { year: "2012", label: { ar: "التأسيس", en: "Foundation" } },
  { year: "2015", label: { ar: "أبراج النخبة", en: "Elite Towers" } },
  { year: "2018", label: { ar: "أول برج شاهق", en: "First High-rise" } },
  { year: "2024", label: { ar: "امتداد عالمي", en: "Global Reach" } },
];

// ── THE GROUP — El-Bagoury ───────────────────────────────────────────
export const GROUP = {
  eyebrow: { ar: "المجموعة الأم", en: "Parent Group" } as Bilingual,
  title: { ar: "مجموعة الباجوري", en: "El-Bagoury Group" } as Bilingual,
  lead: {
    ar: "تحاور العقارية هي جوهرة التاج العقارية لمجموعة الباجوري — تكتّل متنوّع ذو جذورٍ عميقة في الإنشاء واللوجستيات والصناعات الثقيلة.",
    en: "Tahawer Properties is the real estate crown jewel of the El-Bagoury Group — a diversified conglomerate with deep roots in construction, logistics, and heavy industry.",
  } as Bilingual,
  body: {
    ar: "يمنح هذا الدعم المؤسسي شبكة أمانٍ لا مثيل لها لمستثمرينا. فبتحكّمنا في سلسلة التوريد من المواد الخام حتى الإنشاء النهائي، نُزيل المخاطر التي تُلاحق المطوّرين المنفردين.",
    en: "This institutional backing provides an unparalleled safety net for our investors. By controlling the supply chain from raw materials to final construction, we eliminate the risks that plague standalone developers.",
  } as Bilingual,
};

export interface GroupStat { value: string; label: Bilingual }
export const GROUP_STATS: GroupStat[] = [
  { value: "30+", label: { ar: "سنة خبرة", en: "Years Experience" } },
  { value: "4,000+", label: { ar: "موظف", en: "Employees" } },
  { value: "12", label: { ar: "قطاع رئيسي", en: "Major Verticals" } },
  { value: "$1.2B", label: { ar: "محفظة أصول", en: "Asset Portfolio" } },
];

/** `icon` maps to a lucide component in the presentational layer. */
export interface Vertical { icon: string; title: Bilingual; desc: Bilingual }
export const VERTICALS: Vertical[] = [
  { icon: "building2", title: { ar: "الإنشاءات", en: "Construction" }, desc: { ar: "أساسات بمعايير صناعية وتنفيذ الأبراج الشاهقة.", en: "Industrial-grade foundations & high-rise execution." } },
  { icon: "truck", title: { ar: "اللوجستيات", en: "Logistics" }, desc: { ar: "إدارة سلاسل التوريد والمعدات الثقيلة.", en: "Supply chain management and heavy equipment." } },
  { icon: "zap", title: { ar: "الطاقة", en: "Energy" }, desc: { ar: "بنية تحتية مستدامة وحلول الطاقة.", en: "Sustainable infrastructure and power solutions." } },
  { icon: "factory", title: { ar: "التصنيع", en: "Manufacturing" }, desc: { ar: "مواد خام عالية الجودة وتشكيل الصلب.", en: "Quality raw materials and steel fabrication." } },
];

// ── LEADERSHIP ───────────────────────────────────────────────────────
export const LEADERSHIP_INTRO = {
  eyebrow: { ar: "القيادة", en: "Leadership" } as Bilingual,
  title: { ar: "المسؤولون عن كل مشروع.", en: "The people accountable for every project." } as Bilingual,
};

export interface Member { name: Bilingual; role: Bilingual; bio: Bilingual; image: string; linkedin: string }
export const LEADERSHIP: Member[] = [
  { name: { ar: "د. أحمد الباجوري", en: "Dr. Ahmed El-Bagoury" }, role: { ar: "رئيس مجلس الإدارة", en: "Chairman" }, bio: { ar: "بخبرةٍ صناعية تمتد ٣٠ عاماً، يقود د. أحمد التوسّع الاستراتيجي للمجموعة عبر شمال إفريقيا.", en: "With 30 years of industrial expertise, Dr. Ahmed leads the group's strategic expansion across North Africa." }, image: IMG.chairman, linkedin: "#" },
  { name: { ar: "م. سارة محمود", en: "Eng. Sarah Mahmoud" }, role: { ar: "العضو المنتدب", en: "Managing Director" }, bio: { ar: "تشرف م. سارة على خطّ التطوير، لتضمن التقاء التميّز المعماري بالدقة اللوجستية.", en: "Eng. Sarah oversees the development pipeline, ensuring architectural excellence meets logistical precision." }, image: IMG.director, linkedin: "#" },
  { name: { ar: "عمر منصور", en: "Omar Mansour" }, role: { ar: "المدير المالي", en: "Financial Officer" }, bio: { ar: "يدير عمر أدوات الاستثمار والشراكات المصرفية التي تُرسّخ مشروعاتنا متعددة المليارات.", en: "Omar manages the investment vehicles and banking partnerships that anchor our multi-billion dollar projects." }, image: IMG.officer, linkedin: "#" },
];

// ── PARTNERS — The Ecosystem ─────────────────────────────────────────
export const PARTNERS_INTRO = {
  eyebrow: { ar: "المنظومة", en: "The Ecosystem" } as Bilingual,
  title: { ar: "الشركاء والاستشاريون", en: "Partners & Consultants" } as Bilingual,
};

export interface PartnerGroup { category: Bilingual; names: string[] }
export const PARTNER_GROUPS: PartnerGroup[] = [
  { category: { ar: "الهندسة", en: "Engineering" }, names: ["ALLIED", "KHATIB & ALAMI", "ZHA ARCHITECTS"] },
  { category: { ar: "المقاولات", en: "Contracting" }, names: ["ORASCOM", "SIAC", "EL-BAGOURY CONST."] },
  { category: { ar: "المصارف", en: "Banking" }, names: ["ENBD", "HSBC", "QNB"] },
];

export const CERTIFICATIONS: Bilingual[] = [
  { ar: "ISO 9001:2015 لإدارة الجودة", en: "ISO 9001:2015 Quality Mgmt" },
  { ar: "تطويرات معتمدة LEED Gold", en: "LEED Gold Certified Developments" },
  { ar: "مسجّلة لدى EGPB رقم ‎#774-A", en: "EGPB Registered #774-A" },
  { ar: "السلامة والصحة المهنية ISO 45001", en: "Occupational Health Safety ISO 45001" },
];

// ── Closing ──────────────────────────────────────────────────────────
export const ABOUT_CLOSING = {
  title: { ar: "نبني باليقين", en: "Building with Certainty" } as Bilingual,
  lead: { ar: "اختبر الدقة المؤسسية لتحاور العقارية.", en: "Experience the institutional precision of Tahawer Properties." } as Bilingual,
  primary: { ar: "شاهد المشروعات المسلّمة", en: "See Delivered Projects" } as Bilingual,
  secondary: { ar: "تواصل معنا", en: "Contact Us" } as Bilingual,
};
